import { z } from 'zod';
import { PCMP_BATCH_STATUSES } from './pcmpMis.types';
import { PCMP_CHANNELS, PCMP_MKT_APPROVAL_KINDS } from './pcmpProposal.types';
import {
  pcmpPageMetaSchema,
  pcmpQueueItemSchema,
  pcmpQueueSearchShape,
  type PcmpQueueFilters,
} from './pcmpQueue.types';
import { pcmpRequestedItemSchema } from './pcmpRequested.types';

/** The tabs `GET pcmp/marketing/queue` takes. */
export const PCMP_MARKETING_QUEUE_TABS = [
  'AWAITING',
  'ACTIONABLE',
  'ALL',
  'MIS_NOT_VERIFIED',
  'ON_HOLD',
] as const;
export type PcmpMarketingQueueTab = (typeof PCMP_MARKETING_QUEUE_TABS)[number];

/**
 * Proposals with an open Proposal Correction request (legacy LDS-813), for
 * Admins and users granted Proposal Correction; its own endpoint.
 */
export const PCMP_PROPOSAL_CORRECTION_TAB = 'PROPOSAL_CORRECTION';

/** The route batches holding the viewer's proposals (legacy Batches tab, LB-1281). */
export const PCMP_MARKETING_BATCHES_TAB = 'BATCHES';

/** The tabs of the Proposals page, in legacy order (Batches after the queue). */
export const PCMP_MARKETING_TABS = [
  'AWAITING',
  'ACTIONABLE',
  'ALL',
  PCMP_MARKETING_BATCHES_TAB,
  'MIS_NOT_VERIFIED',
  'ON_HOLD',
  PCMP_PROPOSAL_CORRECTION_TAB,
] as const satisfies readonly (
  | PcmpMarketingQueueTab
  | typeof PCMP_MARKETING_BATCHES_TAB
  | typeof PCMP_PROPOSAL_CORRECTION_TAB
)[];
export type PcmpMarketingTab = (typeof PCMP_MARKETING_TABS)[number];

/** The tabs that list proposals (and export them); Batches lists batches. */
export type PcmpMarketingListTab = Exclude<PcmpMarketingTab, typeof PCMP_MARKETING_BATCHES_TAB>;

const rowActionSchema = z.object({
  kind: z.enum(PCMP_MKT_APPROVAL_KINDS),
  levelNo: z.number().nullable(),
  isOptional: z.boolean(),
});

export type PcmpMarketingRowAction = z.infer<typeof rowActionSchema>;

export const pcmpMarketingQueueItemSchema = pcmpQueueItemSchema.extend({
  action: rowActionSchema.nullable(),
  /** The payment proof may be uploaded now (the Marketing payout). */
  canMktDisburse: z.boolean(),
  /** The viewer is the submitting MO of an Accounts payout nobody confirmed yet (LB-1195). */
  canConfirmDisbursement: z.boolean(),
});

export type PcmpMarketingQueueItem = z.infer<typeof pcmpMarketingQueueItemSchema>;

export const paginatedPcmpMarketingQueueSchema = z.object({
  data: z.array(pcmpMarketingQueueItemSchema),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpMarketingQueue = z.infer<typeof paginatedPcmpMarketingQueueSchema>;

export const pcmpMarketingQueueCountsSchema = z.object({
  awaiting: z.number(),
  actionable: z.number(),
  all: z.number(),
  misNotVerified: z.number(),
  misExplained: z.number(),
  onHold: z.number(),
  /** Route batches holding the viewer's proposals, under the filters. */
  batches: z.number(),
  /** Failed Accounts payments awaiting the chain's correction (LB-764). */
  correction: z.number(),
  /** Open Proposal Correction requests; null = the viewer is not offered the tab. */
  proposalCorrection: z.number().nullable(),
});

export type PcmpMarketingQueueCounts = z.infer<typeof pcmpMarketingQueueCountsSchema>;

export const paginatedPcmpProposalCorrectionSchema = z.object({
  data: z.array(pcmpRequestedItemSchema),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpProposalCorrection = z.infer<typeof paginatedPcmpProposalCorrectionSchema>;

export interface PcmpProposalCorrectionParams extends PcmpQueueFilters {
  page: number;
  limit: number;
}

export interface PcmpMarketingQueueParams extends PcmpQueueFilters {
  tab: PcmpMarketingQueueTab;
  explained?: boolean | undefined;
  page: number;
  limit: number;
}

export const pcmpMarketingQueueSearchSchema = z.object({
  tab: z.enum(PCMP_MARKETING_TABS).optional().catch(undefined),
  explained: z.boolean().optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

export type PcmpMarketingQueueSearch = z.infer<typeof pcmpMarketingQueueSearchSchema>;

const batchBucketSchema = z.object({ count: z.number(), amount: z.number() });

/** The viewer's share of a batch, split into exclusive buckets. */
const batchBucketsShape = {
  total: batchBucketSchema,
  pending: batchBucketSchema,
  disbursed: batchBucketSchema,
  disbursementFailed: batchBucketSchema,
  rejected: batchBucketSchema,
  returned: batchBucketSchema,
  held: batchBucketSchema,
};

/** A route batch on the Batches tab (API `GET pcmp/marketing/batches`). */
export const pcmpMarketingBatchSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  batchStatus: z.enum(PCMP_BATCH_STATUSES),
  channel: z.enum(PCMP_CHANNELS).nullable(),
  publishedAt: z.string().nullable(),
  ...batchBucketsShape,
});

export type PcmpMarketingBatch = z.infer<typeof pcmpMarketingBatchSchema>;

export const pcmpMarketingBatchesSchema = z.object({
  /** Over every batch in scope, not just this page. */
  totals: z.object(batchBucketsShape),
  batches: z.object({ items: z.array(pcmpMarketingBatchSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpMarketingBatches = z.infer<typeof pcmpMarketingBatchesSchema>;

/** The Batches tab and a batch's drill-down: the queue filters and paging. */
export interface PcmpMarketingBatchesParams extends PcmpQueueFilters {
  page: number;
  limit: number;
}

export const pcmpMarketingBatchSearchSchema = z.object(pcmpQueueSearchShape);

export type PcmpMarketingBatchSearch = z.infer<typeof pcmpMarketingBatchSearchSchema>;
